/**
 * AI Streaming Service
 * Handles streaming AI responses with ReadableStream processing
 * Based on research patterns from planning phase
 */

import {
  AIPreviewRequest,
  AIPreviewResponse,
  AIPreviewError,
  AIStreamingConfig,
  AIPreviewState,
} from '../pages/search/components/ai-preview/types';

export class AIStreamingService {
  private config: AIStreamingConfig;
  private currentController: AbortController | null = null;

  constructor(config: AIStreamingConfig) {
    this.config = config;
  }

  async streamAIResponse(
    request: AIPreviewRequest,
    onChunk: (chunk: string, isComplete: boolean) => void,
    onError: (error: AIPreviewError) => void
  ): Promise<AIPreviewResponse> {
    this.currentController = new AbortController();

    try {
      const response = await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...this.config.headers,
        },
        body: JSON.stringify({
          prompt: request.prompt,
          session_id: request.session_id,
        }),
        signal: this.currentController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await this.processStream(response, onChunk, onError);
    } catch (error) {
      const aiError = this.createError(error);
      onError(aiError);
      throw aiError;
    }
  }

  private async processStream(
    response: Response,
    onChunk: (chunk: string, isComplete: boolean) => void,
    onError: (error: AIPreviewError) => void
  ): Promise<AIPreviewResponse> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let streamedText = '';
    let fullContent = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.trim() === '') continue;

          if (line === '[DONE]') {
            onChunk(streamedText, true);
            return this.createResponse(fullContent, streamedText, true);
          }

          const processedContent = this.processStreamLine(line);
          if (processedContent) {
            streamedText += processedContent;
            fullContent += processedContent;

            // Throttle updates with 50ms delay for smooth visual streaming
            await new Promise((resolve) => setTimeout(resolve, 50));
            onChunk(streamedText, false);
          }
        }
      }
    } catch (error) {
      const aiError = this.createError(error);
      onError(aiError);
      throw aiError;
    } finally {
      reader.releaseLock();
    }

    return this.createResponse(fullContent, streamedText, true);
  }

  private processStreamLine(line: string): string | null {
    // Handle different stream formats: data, tool_use, mcp_ui
    if (line.startsWith('data: ')) {
      return `${line.substring(6)}\n`;
    }

    if (line.startsWith('tool_use: ')) {
      // Tool use information - could be processed for metadata
      const toolData = line.substring(10);
      try {
        JSON.parse(toolData); // Validate JSON
        return `[Tool: ${toolData}]\n`;
      } catch {
        return '[Tool usage detected]\n';
      }
    }

    if (line.startsWith('mcp_ui: ')) {
      // MCP-UI resources - could be processed for rich content
      const mcpData = line.substring(8);
      try {
        JSON.parse(mcpData); // Validate JSON
        return `[Resource: ${mcpData}]\n`;
      } catch {
        return '[Resource detected]\n';
      }
    }

    return null;
  }

  private createResponse(
    content: string,
    streamedContent: string,
    isComplete: boolean
  ): AIPreviewResponse {
    return {
      content: this.sanitizeContent(content),
      streamedContent: this.sanitizeContent(streamedContent),
      contentType: 'data',
      isComplete,
      metadata: {
        responseTime: Date.now(),
        generatedAt: new Date(),
      },
    };
  }

  private sanitizeContent(content: string): string {
    // Basic XSS prevention - remove potential script tags and dangerous content
    return content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  }

  private createError(error: any): AIPreviewError {
    if (error.name === 'AbortError') {
      return {
        type: 'timeout',
        message: 'Request was cancelled',
        isRetryable: true,
      };
    }

    if (error.message.includes('fetch')) {
      return {
        type: 'network',
        message: 'Network connection failed',
        details: error.message,
        isRetryable: true,
      };
    }

    if (error.message.includes('429')) {
      return {
        type: 'quota',
        message: 'Rate limit exceeded. Please try again later.',
        isRetryable: true,
        retryAfter: 60,
      };
    }

    return {
      type: 'parsing',
      message: 'Failed to process AI response',
      details: error.message,
      isRetryable: false,
    };
  }

  cancelCurrentRequest(): void {
    if (this.currentController) {
      this.currentController.abort();
      this.currentController = null;
    }
  }

  updateConfig(newConfig: Partial<AIStreamingConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}
